import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdEdit, MdDelete, MdAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface FormField {
    _id: string;
    id: string;
    name: string;
    type: string;
    question?: string;
    options?: string[];
    required?: boolean;
    createdAt: string;
    updatedAt: string;
}

export default function BackOfficeFormFields() {
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchFormFields();
    }, []);

    const fetchFormFields = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/form-field`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormFields(response.data.results || response.data.fields || response.data);
        } catch (error) {
            console.error('Error fetching form fields:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/form-field/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormFields(formFields.filter(field => field._id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting form field:', error);
            alert('Failed to delete form field');
        }
    };

    const filteredFormFields = formFields.filter((field) => {
        const matchesSearch =
            field.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            field.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            field.question?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesSearch;
    });

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'string': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
            case 'select': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            case 'multi-select': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
            case 'number': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Form Fields Management
                    </div>
                    <button
                        onClick={() => navigate('/back-office/form-fields/create')}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        <MdAdd className="text-lg" />
                        Add Field
                    </button>
                </div>

                <div className="mb-5">
                    <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MdSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search form fields..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Field ID
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Name
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Question
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Type
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Options
                                    </th>
                                    <th className="pb-3 text-center text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Required
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFormFields.map((field) => (
                                    <tr key={field._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                                            {field.id}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {field.name}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                                            {field.question ? (
                                                <span className="block truncate" title={field.question}>
                                                    {field.question}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="py-4">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getTypeColor(field.type)}`}>
                                                {field.type}
                                            </span>
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {field.options && field.options.length > 0 
                                                ? `${field.options.length} options` 
                                                : '-'}
                                        </td>
                                        <td className="py-4 text-center">
                                            {field.required ? (
                                                <span className="text-green-600 dark:text-green-400">✓</span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => navigate(`/back-office/form-fields/edit/${field._id}`)}
                                                    className="rounded-lg bg-blue-500 p-2 text-white hover:bg-blue-600"
                                                    title="Edit"
                                                >
                                                    <MdEdit />
                                                </button>
                                                {deleteConfirm === field._id ? (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleDelete(field._id)}
                                                            className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="rounded bg-gray-500 px-3 py-1 text-xs text-white hover:bg-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setDeleteConfirm(field._id)}
                                                        className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-600"
                                                        title="Delete"
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredFormFields.length === 0 && (
                            <div className="py-10 text-center text-gray-500">
                                No form fields found
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
